import { type SyntheticEvent, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import {
  getSiteCmsCompetitionCardsApi,
  getSiteCmsImageDataApi,
  listSiteCmsDocumentsApi,
  listSiteCmsImagesApi,
} from '@/lib/api/siteCmsApi';
import { cmsCompetitionReferences, cmsPublicContent, cmsPublicImageReferences } from '@shared/cms/content';
import type { SiteCmsDraftResponse } from '@shared/types/entities';
import { useQueries, useQuery } from '@tanstack/react-query';

import { PREVIEW_LINK_PREFIX, renderPageHtml } from '../../../../../packages/site-renderer/src/render-core.mjs';
import publicStyles from '../../../../../packages/site-renderer/static/site.css?raw';
import { cmsPreviewLinkAction } from './site-cms-preview-navigation';

type Props = {
  draft: SiteCmsDraftResponse;
  pageSlug?: string;
  mobile?: boolean;
  mode?: 'editing' | 'publication';
  onNavigatePage?: (slug: string) => void;
};

/**
 * Script-free sandbox. Its validated, inert links are intercepted by the
 * authenticated parent, which alone decides which private page may be shown.
 */
export default function SiteCmsPreview({
  draft: incoming,
  pageSlug = 'home',
  mobile = false,
  mode = 'editing',
  onNavigatePage,
}: Props) {
  const draft = useDeferredValue(incoming);
  const { ownerType, ownerId } = draft;
  const [localPageSlug, setLocalPageSlug] = useState(pageSlug);
  const [navigationNotice, setNavigationNotice] = useState<{ slug: string; message: string } | null>(null);
  const [loadedHtml, setLoadedHtml] = useState('');
  const releaseFrameListener = useRef<(() => void) | null>(null);
  const pendingFragment = useRef<string | null>(null);
  useEffect(
    () => () => {
      releaseFrameListener.current?.();
    },
    [],
  );
  const displayedSlug = onNavigatePage ? pageSlug : localPageSlug;
  const released = cmsPublicContent(draft.pages, draft.headerMenu);
  const pages = mode === 'publication' ? released.pages : draft.pages;
  const page = pages.find(item => item.slug === displayedSlug) ?? pages[0];
  const refs = [...cmsPublicImageReferences(page ? [page] : [], draft.branding)];
  const ids = cmsCompetitionReferences(page ? [page] : []);
  const images = useQuery({
    queryKey: ['site-cms', 'images', ownerType, ownerId],
    queryFn: () => listSiteCmsImagesApi(ownerType, ownerId),
    retry: false,
  });
  const documents = useQuery({
    queryKey: ['site-cms', 'documents', ownerType, ownerId],
    queryFn: () => listSiteCmsDocumentsApi(ownerType, ownerId),
    retry: false,
  });
  const competitions = useQuery({
    queryKey: ['site-cms', 'competition-cards', ownerType, ownerId, ids.join(',')],
    queryFn: () => getSiteCmsCompetitionCardsApi(ownerType, ownerId, ids),
    enabled: ids.length > 0,
    retry: false,
  });
  const imageData = useQueries({
    queries: refs.map(id => ({
      queryKey: ['site-cms', 'image-data', ownerType, ownerId, id],
      queryFn: () => getSiteCmsImageDataApi(ownerType, ownerId, id),
      staleTime: Infinity,
      retry: false,
    })),
  });
  const imageUrls: Record<string, string> = {};
  refs.forEach((id, index) => {
    const bytes = imageData[index].data;
    if (bytes) imageUrls[id] = bytes;
  });
  const urlsKey = JSON.stringify(imageUrls);
  const rendered = useMemo(() => {
    try {
      const sourcePages = mode === 'publication' ? cmsPublicContent(draft.pages, draft.headerMenu).pages : draft.pages;
      const selected = sourcePages.find(item => item.slug === displayedSlug) ?? sourcePages[0];
      const site = {
        ...draft,
        ...cmsPublicContent(draft.pages, draft.headerMenu),
        images: images.data ?? [],
        documents: documents.data ?? [],
        competitions: competitions.data ?? [],
      };
      return {
        html: selected
          ? renderPageHtml(site, selected, {
              preview: true,
              stylesheet: publicStyles,
              imageUrls: JSON.parse(urlsKey) as Record<string, string>,
            })
          : '',
        error: '',
      };
    } catch {
      return {
        html: '',
        error: 'Le contenu ne peut pas encore être prévisualisé. Corrigez la section signalée puis réessayez.',
      };
    }
  }, [draft, mode, displayedSlug, images.data, documents.data, competitions.data, urlsKey]);
  const pending =
    images.isPending ||
    documents.isPending ||
    imageData.some(q => q.isPending) ||
    (ids.length > 0 && competitions.isPending);
  const failed = images.isError || documents.isError || imageData.some(q => q.isError) || competitions.isError;

  const handleFrameLoad = (event: SyntheticEvent<HTMLIFrameElement>) => {
    releaseFrameListener.current?.();
    releaseFrameListener.current = null;
    const doc = event.currentTarget.contentDocument;
    if (!doc || !doc.documentElement.hasAttribute('data-theme')) {
      setLoadedHtml('');
      setNavigationNotice({
        slug: page?.slug ?? '',
        message: 'Le document de prévisualisation a été interrompu. Fermez puis rouvrez l’aperçu.',
      });
      return;
    }
    const interceptClick = (click: MouseEvent) => {
      const element = click.target as Element | null;
      const anchor = element?.closest?.(`a[href^="${PREVIEW_LINK_PREFIX}"]`);
      if (!anchor) return;
      click.preventDefault();
      click.stopPropagation();
      const action = cmsPreviewLinkAction(
        anchor.getAttribute('href') ?? '',
        pages.map(item => item.slug),
      );
      if (action.kind === 'page') {
        setNavigationNotice(null);
        if (action.slug === page?.slug) {
          if (action.fragment) doc.getElementById(action.fragment)?.scrollIntoView();
          else doc.scrollingElement?.scrollTo(0, 0);
        } else {
          pendingFragment.current = action.fragment;
          if (onNavigatePage) onNavigatePage(action.slug);
          else setLocalPageSlug(action.slug);
        }
      } else if (action.kind === 'fragment') {
        doc.getElementById(action.fragment)?.scrollIntoView();
        setNavigationNotice(null);
      } else {
        setNavigationNotice({
          slug: page?.slug ?? '',
          message:
            action.kind === 'document'
              ? 'Ce PDF se consulte depuis le site publié, pas dans l’aperçu privé.'
              : action.kind === 'external'
                ? 'Le lien externe ne s’ouvre pas depuis l’aperçu privé.'
                : action.kind === 'unavailable'
                  ? 'Cette page n’est pas incluse dans cet aperçu.'
                  : 'Ce lien ne peut pas être ouvert dans l’aperçu privé.',
        });
      }
    };
    doc.addEventListener('click', interceptClick, true);
    releaseFrameListener.current = () => doc.removeEventListener('click', interceptClick, true);
    const fragment = pendingFragment.current;
    if (fragment) doc.getElementById(fragment)?.scrollIntoView();
    pendingFragment.current = null;
    setLoadedHtml(rendered.html);
  };

  return (
    <div className={`cms-preview ${mobile ? 'cms-preview-mobile' : ''}`}>
      <div className='cms-preview-chrome'>
        <span aria-hidden='true'>● ● ●</span>
        <span>
          {mode === 'publication'
            ? 'Prochaine publication · pages incluses uniquement'
            : 'Édition privée · inclut les pages non publiées'}
        </span>
      </div>
      <div className='cms-preview-status' role='status'>
        {rendered.error
          ? 'Aperçu indisponible.'
          : !page
            ? 'Aucune page à prévisualiser.'
            : failed
              ? 'Aperçu partiel — vérifiez les ressources.'
              : pending
                ? 'Chargement des ressources privées…'
                : draft !== incoming
                  ? 'Actualisation de l’aperçu…'
                  : loadedHtml !== rendered.html
                    ? 'Préparation du rendu…'
                    : 'Aperçu prêt — naviguez entre les pages depuis les liens du site.'}
      </div>
      {navigationNotice?.slug === page?.slug && (
        <p className='cms-notice' role='status'>
          {navigationNotice.message}
        </p>
      )}
      {mode === 'editing' && page?.visibility === 'DRAFT' && (
        <p className='cms-alert'>Cette page est privée : elle ne sera pas diffusée lors de la publication.</p>
      )}
      {failed && (
        <p role='alert' className='cms-alert'>
          Aperçu partiel : certaines ressources sont indisponibles.{' '}
          <button
            type='button'
            onClick={() => {
              void images.refetch();
              void documents.refetch();
              if (ids.length) void competitions.refetch();
              imageData.forEach(q => void q.refetch());
            }}
          >
            Réessayer
          </button>
        </p>
      )}
      {rendered.error ? (
        <p role='alert' className='cms-alert'>
          {rendered.error}
        </p>
      ) : !page ? (
        <p className='cms-notice'>Incluez au moins une page dans cette version pour la vérifier.</p>
      ) : (
        <iframe
          title={
            mode === 'publication' ? 'Vérification de la prochaine publication' : 'Aperçu du site — brouillon privé'
          }
          srcDoc={rendered.html}
          onLoad={handleFrameLoad}
          sandbox='allow-same-origin'
          referrerPolicy='no-referrer'
        />
      )}
    </div>
  );
}
